import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { IPet } from '@/shared/interfaces';
import { PetTypeEnum } from '@/shared/enums';
import { R } from '@/features/profile/ProfileResources.resx';
import { Button } from '@/components';
import { useState } from 'react';
import { PetModal } from '@/features/profile/pets/PetModal';
import { useHydrateAtoms } from 'jotai/utils';
import { petsAtom } from '@/features/profile/pets/petAtoms';
import { useAtom } from 'jotai/index';
import {
    QueryClient,
    QueryClientProvider,
    useMutation,
    useQuery,
} from '@tanstack/react-query';
import { deletePet } from '@/api/profile/deletePet';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { toast } from '@/shared/Toast';
import { fetchPets } from '@/api/profile/fetchPets';

export function PetsApp(containerQuery: string, pets: IPet[]) {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    const queryClient = new QueryClient();

    root.render(
        <QueryClientProvider client={queryClient}>
            <Pets bootstrappedPets={pets} />
        </QueryClientProvider>,
    );
}

type PetProps = {
    bootstrappedPets: IPet[];
};

const iconPath: (petType: PetTypeEnum) => string = (petType) => {
    switch (petType) {
        case PetTypeEnum.Dog:
            return '/images/icons/pet-dog.svg';
        case PetTypeEnum.Cat:
            return '/images/icons/pet-cat.svg';
        case PetTypeEnum.Rabbit:
            return '/images/icons/pet-rabbit.svg';
        case PetTypeEnum.Rodent:
            return '/images/icons/pet-rodent.svg';
        default:
            return '/images/icons/pet-dog.svg';
    }
};

const Pets: ({ bootstrappedPets }: PetProps) => React.ReactNode = ({
    bootstrappedPets,
}) => {
    useHydrateAtoms([[petsAtom, bootstrappedPets]]);
    const [pets, setPets] = useAtom(petsAtom);
    const [selectedPet, setSelectedPet] = useState<IPet>();
    const [isPetModalOpen, setIsPetModalOpen] = useState(false);
    const [petToDelete, setPetToDelete] = useState<IPet | null>(null);

    const openPetModal = (pet: IPet | null): void => {
        setSelectedPet(pet);
        setIsPetModalOpen(true);
    };

    const { refetch } = useQuery({
        queryKey: ['pets'],
        queryFn: fetchPets,
        enabled: false,
    });

    const handleRefetch = async () => {
        try {
            const { data: pets } = await refetch();
            setPets(pets);
        } catch (e) {
            window.location.reload();
        }
    };

    const deletePetMutation = useMutation({
        mutationFn: deletePet,
        onSuccess: handleRefetch,
        onSettled: () => setPetToDelete(null),
        onError: () => {
            toast.addToast({
                type: 'error',
                message: R.ErrorDeletePet,
            });
        },
    });

    return (
        <>
            <div
                className="my-5 flex flex-col gap-2"
                aria-describedby="pets-profile-description"
            >
                {pets.map((pet) => (
                    <div
                        className="flex flex-row items-center gap-4"
                        key={pet.Id}
                    >
                        <button
                            className="flex w-full max-w-sm items-center justify-between rounded-2xl border border-grey-300 p-4 text-left hover:bg-grey-100"
                            tabIndex={0}
                            aria-labelledby="pet-name edit-action"
                            onClick={() => openPetModal(pet)}
                        >
                            <img
                                className="mr-4 flex-none"
                                width="40"
                                height="40"
                                src={iconPath(pet.Type)}
                                alt=""
                            />
                            <div className="flex-1">
                                <div className="text-lg font-bold leading-tight text-grey-900">
                                    {pet.Name}
                                </div>
                                <div className="text-sm font-normal leading-tight text-grey-700">
                                    {pet.Race}
                                </div>
                            </div>
                            <img
                                id="edit-action"
                                className="flex-none"
                                width="24"
                                height="24"
                                src="/images/icons/edit.svg"
                                alt={R.OpenEditPetModalA11y}
                            />
                        </button>

                        <button aria-labelledby="pet-name delete-action">
                            <img
                                id="delete-action"
                                className="block cursor-pointer"
                                width="24"
                                height="24"
                                src="/images/icons/trash.svg"
                                alt={R.OpenDeletePetModalA11y}
                                onClick={() => setPetToDelete(pet)}
                            />
                        </button>
                    </div>
                ))}
            </div>

            <Button variant="primary" onClick={() => openPetModal(null)}>
                {R.AddPet}
            </Button>

            {isPetModalOpen && (
                <PetModal
                    pet={selectedPet}
                    onClose={(refetch) => {
                        if (refetch) {
                            handleRefetch();
                        }
                        setIsPetModalOpen(false);
                    }}
                ></PetModal>
            )}

            {petToDelete && (
                <ConfirmationModal
                    title={R.RemovePet}
                    onClose={() => setPetToDelete(null)}
                    message={R.RemovePetText}
                    confirmButtonText={R.RemovePet}
                    confirmButtonClick={() =>
                        deletePetMutation.mutate(petToDelete)
                    }
                    cancelButtonText={R.KeepPet}
                    confirmIsProcessing={deletePetMutation.isPending}
                />
            )}
        </>
    );
};
